//#region import libraries
import React, { useEffect, useState } from 'react';

// styling
import { Spacing } from '_styles';

// bootstrap
import { Button } from "react-bootstrap";
//utils
import { makeExcel } from '_utils/makeExcel';
//Moment to format date
import moment from 'moment';
//redux
import { useSelector } from 'react-redux';
import { selectAirportBase64Logo } from '_redux/slices/analytics/reviews';
import { selectIncludeAssistanceBreakdown, selectSelectedReportType, selectPassengerBreakdownAssistanceTotals } from '_redux/slices/timeline-reports/timeline-reports';
//assistance code display order
import { TARGET_CODES } from '_utils/constants';
//component
import { CustomSpinner } from '_components/atoms';
//#endregion


const TimelineReportFooter = ({ passengersData, iata, dateRange, statusLoading }) => {

    //#region redux
    const airportBase64Logo = useSelector(selectAirportBase64Logo);
    const reportType = useSelector(selectSelectedReportType) || 'passenger-records';
    const includeAssistanceBreakdown = useSelector(selectIncludeAssistanceBreakdown);
    const assistanceTotals = useSelector(selectPassengerBreakdownAssistanceTotals);
    //#endregion

    //#region state
    const [loading, setLoading] = useState(false);
    const [fileType, setFileType] = useState("EXCEL")
    //#endregion


    //#region function

    // sum columns 1..N across all rows and append a "TOTAL" row at the end
    function appendTotalsRow(data) {
        if (!Array.isArray(data) || data.length === 0) return data;

        const cols = data[0].length;
        const totals = new Array(cols).fill(0);
        totals[0] = "TOTAL";

        for (const row of data) {
            // guard against ragged rows
            for (let c = 1; c < Math.min(cols, row.length); c++) {
                const n = Number(row[c]);
                totals[c] += Number.isFinite(n) ? n : 0;
            }
        }
        return [...data, totals];
    }
    //CSV functions
    function escapeCSV(v) {
        if (v === null || v === undefined) return "";
        const s = String(v);
        // quote if it has comma, quote, or newline
        if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
    }
    //Convert data into line/strings
    function toCSV(headers, rows) {
        const head = headers.map(escapeCSV).join(",");
        const body = rows.map(r => r.map(escapeCSV).join(",")).join("\r\n");
        return `${head}\r\n${body}`;
    }
    //final step to download CSV file
    function downloadCSV(csvString, filename = "data.csv") {
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        setLoading(false)

        document.body.removeChild(link);
    }


    const handleExcel = async () => {
        setFileType("EXCEL")
        setLoading(true)

        const dataWithTotalRow = (passengersData.data?.length === 1 || reportType === "passenger-records") ? passengersData.data : appendTotalsRow(passengersData.data)
        const startDate = moment(new Date(dateRange[0])).format().split("T")[0];
        const endDate = moment(new Date(dateRange[1] ? dateRange[1] : dateRange[0])).format().split("T")[0];
        const fileName = `${reportType}${includeAssistanceBreakdown ? "_assistances" : ""}_${startDate}-${endDate}`
        let data = {};
        data.fileName = fileName;
        data.workSheetName = [fileName];

        const columnTitles = passengersData.columns.map(c => c.name);
        data.columnTitles = [columnTitles];
        data.columnWidth = [columnTitles.map((row) => row.length + 10)];
        const excelData = dataWithTotalRow
        data.rows = [excelData];

        // for the passenger breakdown, add a totals + per-code assistance summary row and drop the PRM logo
        const isPassengerBreakdown = reportType === 'passenger-breakdown';
        let assistRow = null;
        if (isPassengerBreakdown && assistanceTotals) {
            const pairs = [
                `Total: ${assistanceTotals.grandTotal}`,
                ...[...TARGET_CODES, 'OTHER'].map(code => `${code}: ${assistanceTotals.totals[code] || 0}`),
            ];
            assistRow = ['PRM assistances', pairs.join(' | ')];
        }

        await makeExcel(data, iata, airportBase64Logo, assistRow, !!assistRow, isPassengerBreakdown);
        setLoading(false)
    }
    //function to generate CSV file
    const handleDownloadCSV = () => {
        setFileType("CSV")
        setLoading(true)
        const dataWithTotalRow =
            (passengersData.data?.length === 1 || reportType === "passenger-records")
                ? passengersData.data
                : appendTotalsRow(passengersData.data);

        const startDate = moment(new Date(dateRange[0])).format("YYYY-MM-DD");
        const endDate = moment(new Date(dateRange[1] ?? dateRange[0])).format("YYYY-MM-DD");

        const fileName = `${reportType}${includeAssistanceBreakdown ? "_assistances" : ""}_${startDate}-${endDate}.csv`;

        const headers = (passengersData.columns || []).map(c => c.name);
        const csv = toCSV(headers, dataWithTotalRow);

        downloadCSV(csv, fileName);
    };

    //#endregion

    //#region render Export buttons/Spinner

    return (
        <div style={{ marginTop: Spacing.SCALE_12, display: 'flex', justifyContent: 'space-between' }}>

            {/* last updated */}
            <div></div>

            {/* export button */}
            <div>

                {/* only show button if there is data to export */}
                {passengersData && passengersData.data.length > 0 &&
                    <>

                        <Button
                            variant={"outline-secondary"}
                            onClick={handleExcel}                  // add download as csv
                            disabled={loading || statusLoading}                    // disable when loading
                            loading={true}                     // show loading when loading
                            style={{ width: 180, marginLeft: 15 }}
                        >
                            {loading && fileType === "EXCEL" ? <CustomSpinner /> : "Export Excel"}
                        </Button>
                        <Button
                            variant="outline-secondary"
                            onClick={handleDownloadCSV}
                            disabled={loading || statusLoading}
                            loading={true}
                            style={{ width: 180, marginLeft: 15 }}
                        >
                            {loading && fileType === "CSV" ? <CustomSpinner /> : "Export CSV"}
                        </Button>
                    </>
                }

            </div>

        </div>
    );
};

export default TimelineReportFooter;
